import {
  blockOnErrorIssues,
  type DataImportConfirmDecision,
  type DataImportConfirmPolicy,
  type FormState,
} from './dataImport.config';
import { type ParsedImportFile } from './dataImport.parsing';
import { type DataImportPreviewModel } from './dataImport.preview';
import {
  DEFAULT_DATA_IMPORT_WIZARD_STEPS,
  type DataImportWizardStepId,
  type DataImportWizardSteps,
  getPreviousDataImportWizardStep,
  hasDataImportMappingStep,
} from './dataImportWizard.steps';

export type ReadyState<T> = { status: 'idle' } | { status: 'ready'; value: T };

export type DataImportWizardState<TPreview, TResult, TParsedData = unknown> = {
  /** `'result'` is the implicit state after the last configured step succeeds. */
  activeStep: DataImportWizardStepId | 'result';
  columnMapping?: Record<string, string>;
  file?: File;
  formState: FormState;
  importError?: Error;
  isColumnMappingValid: boolean;
  isImporting: boolean;
  isParsing: boolean;
  isValidating: boolean;
  parseError?: Error;
  parsedData: ReadyState<TParsedData>;
  parsedFile?: ParsedImportFile;
  preview: ReadyState<TPreview>;
  previewError?: Error;
  previewModel: ReadyState<DataImportPreviewModel>;
  result: ReadyState<TResult>;
};

export type DataImportWizardAction<TPreview, TResult, TParsedData = unknown> =
  | { type: 'back' }
  | { type: 'fileParseFailed'; error: Error }
  | { type: 'fileParsed'; parsedFile: ParsedImportFile }
  | { type: 'fileRemoved' }
  | { type: 'fileSelected'; file: File }
  | { type: 'formUpdated'; formState: FormState }
  | { type: 'importFailed'; error: Error }
  | { type: 'importStarted' }
  | { type: 'importSucceeded'; result: TResult }
  | {
      type: 'mappingUpdated';
      columnMapping: Record<string, string>;
      isValid: boolean;
    }
  | { type: 'previewFailed'; error: Error }
  | { type: 'previewStarted'; previewModel: DataImportPreviewModel }
  | {
      type: 'previewSucceeded';
      parsedData: ReadyState<TParsedData>;
      preview: TPreview;
      previewModel: DataImportPreviewModel;
    }
  | { type: 'setStep'; step: DataImportWizardStepId };

export function createDataImportWizardState<
  TPreview,
  TResult,
  TParsedData = unknown,
>(): DataImportWizardState<TPreview, TResult, TParsedData> {
  return {
    activeStep: 'file',
    formState: {
      data: {},
      errors: {},
      isValid: false,
    },
    isColumnMappingValid: false,
    isImporting: false,
    isParsing: false,
    isValidating: false,
    parsedData: { status: 'idle' },
    preview: { status: 'idle' },
    previewModel: { status: 'idle' },
    result: { status: 'idle' },
  };
}

export type ConfirmableDataImportWizardState<
  TPreview,
  TResult,
  TParsedData = unknown,
> = DataImportWizardState<TPreview, TResult, TParsedData> & {
  parsedData: { status: 'ready'; value: TParsedData };
  preview: { status: 'ready'; value: TPreview };
  previewModel: { status: 'ready'; value: DataImportPreviewModel };
};

/**
 * The half of confirmability no import definition can influence: nothing in
 * flight, and parsed data, a preview and a preview model to submit. A policy
 * only ever sees a state that passed this, so a local schema failure — which
 * leaves `parsedData` idle — stays blocking however permissive the policy is.
 */
function isDataImportStructurallyReady<
  TPreview,
  TResult,
  TParsedData = unknown,
>(
  state: DataImportWizardState<TPreview, TResult, TParsedData>,
): state is ConfirmableDataImportWizardState<TPreview, TResult, TParsedData> {
  return (
    !state.isImporting &&
    !state.isValidating &&
    state.parsedData.status === 'ready' &&
    state.preview.status === 'ready' &&
    state.previewModel.status === 'ready'
  );
}

/**
 * The decision behind the confirm action. A state that is not structurally
 * ready yields a blocked decision without a reason: no policy ran, so there is
 * no explanation to show next to the button.
 */
export function getDataImportConfirmDecision<
  TPreview,
  TResult,
  TParsedData = unknown,
>(
  state: DataImportWizardState<TPreview, TResult, TParsedData>,
  policy: DataImportConfirmPolicy<TPreview> = blockOnErrorIssues,
): DataImportConfirmDecision {
  if (!isDataImportStructurallyReady(state)) return { allowed: false };

  return policy({
    model: state.previewModel.value,
    preview: state.preview.value,
  });
}

export function canConfirmDataImport<TPreview, TResult, TParsedData = unknown>(
  state: DataImportWizardState<TPreview, TResult, TParsedData>,
  policy?: DataImportConfirmPolicy<TPreview>,
): state is ConfirmableDataImportWizardState<TPreview, TResult, TParsedData> {
  return (
    isDataImportStructurallyReady(state) &&
    getDataImportConfirmDecision(state, policy).allowed
  );
}

/**
 * Sequence navigation, the one part of the reducer a configured flow changes:
 * where `back` lands, and — when a flow omits the mapping step — deriving an
 * identity column mapping as soon as a file parses, so the file step's
 * primary action can run preview validation directly instead of a mapping UI
 * nothing points at.
 */
export function dataImportWizardReducer<
  TPreview,
  TResult,
  TParsedData = unknown,
>(
  state: DataImportWizardState<TPreview, TResult, TParsedData>,
  action: DataImportWizardAction<TPreview, TResult, TParsedData>,
  steps: DataImportWizardSteps = DEFAULT_DATA_IMPORT_WIZARD_STEPS,
): DataImportWizardState<TPreview, TResult, TParsedData> {
  switch (action.type) {
    case 'back': {
      const previousStep =
        state.activeStep === 'result'
          ? undefined
          : getPreviousDataImportWizardStep(steps, state.activeStep);
      return {
        ...state,
        activeStep: previousStep ?? state.activeStep,
        importError: undefined,
      };
    }
    case 'fileSelected':
      return {
        ...state,
        columnMapping: undefined,
        file: action.file,
        importError: undefined,
        isColumnMappingValid: false,
        isParsing: true,
        isValidating: false,
        parsedData: { status: 'idle' },
        parsedFile: undefined,
        parseError: undefined,
        preview: { status: 'idle' },
        previewError: undefined,
        previewModel: { status: 'idle' },
        result: { status: 'idle' },
      };
    case 'fileRemoved':
      return {
        ...state,
        columnMapping: undefined,
        file: undefined,
        isColumnMappingValid: false,
        isParsing: false,
        isValidating: false,
        parsedData: { status: 'idle' },
        parsedFile: undefined,
        parseError: undefined,
        preview: { status: 'idle' },
        previewError: undefined,
        previewModel: { status: 'idle' },
        result: { status: 'idle' },
      };
    case 'fileParsed': {
      if (hasDataImportMappingStep(steps)) {
        return { ...state, isParsing: false, parsedFile: action.parsedFile };
      }

      // No mapping step configured: the source headers are the mapping.
      const identityColumnMapping = Object.fromEntries(
        action.parsedFile.headerFields.map((header) => [header, header]),
      );
      return {
        ...state,
        columnMapping: identityColumnMapping,
        isColumnMappingValid: true,
        isParsing: false,
        parsedFile: action.parsedFile,
      };
    }
    case 'fileParseFailed':
      return { ...state, isParsing: false, parseError: action.error };
    case 'formUpdated':
      return {
        ...state,
        formState: action.formState,
        isValidating: false,
        parsedData: { status: 'idle' },
        preview: { status: 'idle' },
        previewError: undefined,
        previewModel: { status: 'idle' },
        result: { status: 'idle' },
      };
    case 'mappingUpdated':
      return {
        ...state,
        columnMapping: action.columnMapping,
        isColumnMappingValid: action.isValid,
        isValidating: false,
        parsedData: { status: 'idle' },
        preview: { status: 'idle' },
        previewError: undefined,
        previewModel: { status: 'idle' },
        result: { status: 'idle' },
      };
    case 'previewStarted':
      return {
        ...state,
        activeStep: 'preview',
        importError: undefined,
        isValidating: true,
        parsedData: { status: 'idle' },
        preview: { status: 'idle' },
        previewError: undefined,
        previewModel: { status: 'ready', value: action.previewModel },
      };
    case 'previewFailed':
      return {
        ...state,
        isValidating: false,
        previewError: action.error,
      };
    case 'previewSucceeded':
      return {
        ...state,
        isValidating: false,
        parsedData: action.parsedData,
        preview: { status: 'ready', value: action.preview },
        previewModel: { status: 'ready', value: action.previewModel },
      };
    case 'importStarted':
      return { ...state, importError: undefined, isImporting: true };
    case 'importFailed':
      return { ...state, importError: action.error, isImporting: false };
    case 'importSucceeded':
      return {
        ...state,
        activeStep: 'result',
        isImporting: false,
        result: { status: 'ready', value: action.result },
      };
    case 'setStep':
      return { ...state, activeStep: action.step };
  }
}

/** Binds the reducer to a configured flow, so a host does not thread `steps` through every dispatch. */
export function createDataImportWizardReducer<
  TPreview,
  TResult,
  TParsedData = unknown,
>(steps: DataImportWizardSteps) {
  return (
    state: DataImportWizardState<TPreview, TResult, TParsedData>,
    action: DataImportWizardAction<TPreview, TResult, TParsedData>,
  ) => dataImportWizardReducer(state, action, steps);
}
